// HTTP / SSE 客户端——所有请求自动注入 JWT。
//
// 401 处理：拦截到 server 返回 401 → 清掉 session → 抛 AuthError，让上层路由跳回 Login。
// 之所以不在这里直接 navigate：fetch 模块不应该耦合路由 / UI；上层用 AuthError 自己决定。

import { settings } from './settings'
import { getSession, clearSession } from './auth'

const log = settings.debug ? console.log.bind(console, '[api]') : () => {}

export class AuthError extends Error {
    constructor(message = 'unauthorized') {
        super(message)
        this.name = 'AuthError'
    }
}

// 携带 HTTP status，让上层(如 Login)能按状态码把报错定位到对应表单字段
export class ApiError extends Error {
    status: number
    constructor(message: string, status: number) {
        super(message)
        this.name = 'ApiError'
        this.status = status
    }
}

function buildHeaders(extra: Record<string, string> = {}): Record<string, string> {
    const headers: Record<string, string> = { 'content-type': 'application/json', ...extra }
    const session = getSession()
    if (session) headers['Authorization'] = `Bearer ${session.accessToken}`
    return headers
}

async function handleResponse(resp: Response, method: string, path: string, auth: boolean): Promise<unknown> {
    if (resp.ok) return resp.json()

    const body = await resp.text()
    log(method, path, 'FAIL', resp.status, body)
    let detail: string | undefined
    try {
        detail = (JSON.parse(body) as { detail?: string }).detail
    } catch {
        /* not JSON */
    }

    // 受保护请求的 401 = token 失效 → 清 session 让上层跳登录；
    // 公开端点(login/register)的 401 = 凭证错误，正常把 detail 展示出来
    if (resp.status === 401 && auth) {
        clearSession()
        throw new AuthError(detail)
    }
    throw new ApiError(detail || `HTTP ${resp.status}`, resp.status)
}

// 二进制响应(TTS mp3)的 POST。不走只返回 JSON 的 request()，但复用 JWT 注入 + 同样的错误语义。
async function postBinary(path: string, data: unknown): Promise<ArrayBuffer> {
    const resp = await fetch(settings.API_BASE + path, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(data),
    })
    if (resp.ok) return resp.arrayBuffer()

    const body = await resp.text()
    log('POST', path, 'FAIL', resp.status, body)
    let detail: string | undefined
    try {
        detail = (JSON.parse(body) as { detail?: string }).detail
    } catch {
        /* not JSON */
    }
    if (resp.status === 401) {
        clearSession()
        throw new AuthError(detail)
    }
    throw new ApiError(detail || `HTTP ${resp.status}`, resp.status)
}

interface RequestOpts {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    data?: unknown
    params?: Record<string, string | number | undefined | null>
    timeoutMs?: number
    auth?: boolean // 默认 true；register/login 这种公开 endpoint 传 false
}

async function request<T = unknown>(path: string, opts: RequestOpts = {}): Promise<T> {
    const { method = 'POST', data, params, timeoutMs = 30000, auth = true } = opts
    let url = settings.API_BASE + path
    if (params) {
        const qs = new URLSearchParams()
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== null) qs.set(k, String(v))
        }
        const s = qs.toString()
        if (s) url += '?' + s
    }

    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), timeoutMs)
    try {
        const resp = await fetch(url, {
            method,
            headers: auth ? buildHeaders() : { 'content-type': 'application/json' },
            body: data !== undefined ? JSON.stringify(data) : undefined,
            signal: ctrl.signal,
        })
        const result = await handleResponse(resp, method, path, auth)
        log(method, path, '->', resp.status)
        return result as T
    } finally {
        clearTimeout(timer)
    }
}

// ───── SSE 流式 POST ─────

export interface ChatStreamEvent {
    content?: string
    sentence?: string
    index?: number
    done?: boolean
    error?: string
    usage?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number }
    conversationId?: number // done 时回传:这一轮落库后的真实会话 id,前端据此替换本地假 id
}

export interface StreamHandle extends Promise<void> {
    cancel(): void
    isAborted(): boolean
}

function streamPost(path: string, data: unknown, onEvent: (e: ChatStreamEvent) => void): StreamHandle {
    const ctrl = new AbortController()
    let aborted = false

    const promise = (async () => {
        const resp = await fetch(settings.API_BASE + path, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify(data),
            signal: ctrl.signal,
        })
        if (resp.status === 401) {
            clearSession()
            throw new AuthError()
        }
        if (!resp.ok) {
            const body = await resp.text()
            throw new Error(`HTTP ${resp.status}: ${body}`)
        }
        if (!resp.body) throw new Error('SSE: no response body')

        const reader = resp.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''
        while (true) {
            const { done, value } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            let idx: number
            while ((idx = buffer.indexOf('\n\n')) !== -1) {
                const rawEvent = buffer.slice(0, idx).trim()
                buffer = buffer.slice(idx + 2)
                if (!rawEvent.startsWith('data:')) continue
                const payload = rawEvent.slice(5).trim()
                try {
                    onEvent(JSON.parse(payload) as ChatStreamEvent)
                } catch (e) {
                    console.warn('[api] SSE parse error:', e, payload)
                }
            }
        }
    })()

    // 注意：cancel/isAborted 必须挂在最终返回的 promise 上。.catch() 会产生新 promise，
    // 之前挂在中间 promise 上的方法会丢，导致 handle.cancel 为 undefined。
    const handle = promise.catch((err: unknown) => {
        if (aborted || (err instanceof Error && err.name === 'AbortError')) return
        throw err
    }) as StreamHandle
    handle.cancel = () => {
        aborted = true
        ctrl.abort()
    }
    handle.isAborted = () => aborted
    return handle
}

// ───── 业务类型 ─────

export interface AuthResponse {
    accessToken: string
    userId: string
    username: string
}

export interface UserPrefs {
    userId: string
    username: string
    persona: string
    theme: 'light' | 'dark'
}

export interface ChatRequest {
    message: string
    persona: string
    mode?: 'chat' | 'vocabulary' | 'translate'
    vocabulary?: { word: string }[]
}

export interface HistoryRow {
    _id: number
    userText: string
    aiText: string
    createdAt?: string
}

export interface HistoryListResponse {
    list: HistoryRow[]
    nextCursor: number | null
}

// ───── 业务端点 ─────

export const api = {
    async health(): Promise<{ status: string }> {
        const resp = await fetch(settings.API_BASE + '/health', {
            method: 'GET',
            signal: AbortSignal.timeout(3000),
        })
        if (!resp.ok) throw new Error(`health HTTP ${resp.status}`)
        return resp.json() as Promise<{ status: string }>
    },

    auth: {
        register(username: string, password: string, inviteCode: string): Promise<AuthResponse> {
            return request<AuthResponse>('/api/auth/register', {
                method: 'POST',
                data: { username, password, inviteCode },
                auth: false,
            })
        },
        login(username: string, password: string): Promise<AuthResponse> {
            return request<AuthResponse>('/api/auth/login', {
                method: 'POST',
                data: { username, password },
                auth: false,
            })
        },
        me(): Promise<UserPrefs> {
            return request<UserPrefs>('/api/auth/me', { method: 'GET' })
        },
    },

    user: {
        get(): Promise<UserPrefs> {
            return request<UserPrefs>('/api/user/me', { method: 'GET' })
        },
        update(fields: Partial<Pick<UserPrefs, 'persona' | 'theme'>>): Promise<UserPrefs> {
            return request<UserPrefs>('/api/user/me', { method: 'PUT', data: fields })
        },
    },

    history: {
        list(mode: string, opts: { pageSize?: number; beforeId?: number } = {}): Promise<HistoryListResponse> {
            const { pageSize = 20, beforeId } = opts
            return request<HistoryListResponse>('/api/history', {
                method: 'GET',
                params: { mode, pageSize, beforeId },
            })
        },
    },

    tts: {
        // 服务端代理合成一句 → 整段 mp3 字节(浏览器不能直连 DashScope)
        synthesizeAliyun(text: string, persona: string): Promise<ArrayBuffer> {
            return postBinary('/api/tts/synthesize', { text, persona })
        },
    },

    asr: {
        // 实时 ASR WS 代理地址。WS 不能设 header,JWT 走 query;同源(dev 经 vite 反代、prod 经 Caddy)
        wsUrl(engine = '16k_zh_en'): string {
            const token = getSession()?.accessToken ?? ''
            const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
            return `${proto}//${location.host}/api/asr/ws?token=${encodeURIComponent(token)}&engine=${encodeURIComponent(engine)}`
        },
    },

    chatStream(payload: ChatRequest, onEvent: (e: ChatStreamEvent) => void): StreamHandle {
        return streamPost('/api/chat', payload, onEvent)
    },
}
